import React, { useState } from 'react';
import moment from 'moment';
import Avatar from 'src/components/Avatar';
import FontAwesomeIcon from 'src/components/Icon';
import Button from 'src/components/Button';
import TextEditor from 'src/components/Editor/Editor';
import './notes-repeater.scss';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { Attachments } from 'src/pages/ServiceRequests/components/Attachments';
import { isImage } from 'src/helpers/CommonHelpers';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import {
  usePinnedResource,
  UsePinnedResourceReturn,
} from 'src/pages/ServiceRequest/components/PinnedResource/CustomHooks/usePinnedResource';

const NOTE_TRUNCATE_LENGTH = 300;

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

const NotesRepeater = ({
  onEdit,
  onDelete,
  notes,
  onImageClicked,
  user,
  enableEdit = true,
  hideDelete = false,
  serviceRequestId,
  serviceRequestNumber,
  onNotePinned,
}: any) => {
  const [noteToDelete, setNoteToDelete] = useState<any>(null);
  const [expandedNote, setExpandedNote] = useState<any>(null);
  const [noteToPin, setNoteToPin] = useState<any>(null);

  const { pinResource, loading: isPinning }: UsePinnedResourceReturn =
    usePinnedResource();

  const roleName: string = user.roles[0].role.name;

  const canShowActions: boolean = !(
    roleName === 'Home Owner' ||
    roleName === 'Realtor' ||
    roleName === 'Occupant'
  );

  const isAdminRole: boolean =
    roleName === 'Admin' ||
    roleName === 'Super Admin'

  const canEditNote = (note: any): boolean =>
    canShowActions && (isAdminRole || note?.author?.id === user.id);

  const canDeleteNote = (note: any): boolean =>
    canShowActions && (isAdminRole || note?.author?.id === user.id);

  const handleConfirmDelete = () => {
    if (noteToDelete) {
      onDelete(noteToDelete);
      setNoteToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setNoteToDelete(null);
  };

  const handleConfirmPin = async () => {
    if (!noteToPin) {
      return;
    }

    await pinResource(serviceRequestId, noteToPin.id);
    onNotePinned?.();
    setNoteToPin(null);
  };

  const handleCancelPin = () => {
    setNoteToPin(null);
  };

  const pinConfirmMessage: string = serviceRequestNumber
    ? `Pin this note to service request #${serviceRequestNumber}?`
    : 'Pin this note to this service request?';

  return (
    <>
      <div className="note-card-scroll-wrapper">
        {notes.map((note: any, x: number) => {
          const isLong = stripHtml(note.body ?? '').length > NOTE_TRUNCATE_LENGTH;

          return (
            <div key={x} className="note-card">
              {canShowActions && serviceRequestId && (
                <button
                  type="button"
                  className="note-card-pin"
                  aria-label="Pin note"
                  onClick={() => setNoteToPin(note)}
                >
                  <FontAwesomeIcon glyph="thumbtack" weight="solid" />
                </button>
              )}
              <div className="note-card-content">
                {(note.appointment || note.title) && (
                  <div className="note-card-title">
                    {note.appointment && <>[Reminder] </>}
                    {note.title}
                  </div>
                )}
                {note.appointment && (
                  <div className="note-card-appointment">
                    <FontAwesomeIcon glyph="calendar" weight="solid" size="medium" />
                    {moment(note?.appointment.date).format('dddd MM/DD/YYYY')}
                  </div>
                )}
                <div className="note-card-body">
                  <div className={isLong ? 'note-card-body-truncated' : ''}>
                    <div className="note-card-editor-clean">
                      <TextEditor
                        editorState={note.body}
                        readOnly={true}
                        onEditorStateChange={null}
                      />
                    </div>
                  </div>
                  {isLong && (
                    <span
                      className="note-card-read-more"
                      onClick={() => setExpandedNote(note)}
                    >
                      Read more
                    </span>
                  )}
                </div>
                <div className="note-card-footer">
                  <div className="note-card-author">
                    <div className="note-card-avatar">
                      <Avatar size="small" />
                    </div>
                    <div className="note-card-username">
                      {note?.author?.firstName} {note?.author?.lastName}
                    </div>
                    <div className="note-card-date">
                      {moment(note?.createdAt).format('MM/DD/YYYY hh:mm A')}
                    </div>
                    {note?.image && note?.image.length > 1 ? (
                      isImage(note.image) ? (
                        <div className="note-card-attachment">
                          <Attachments
                            attachments={{
                              hasAttachment: true,
                              firstCompressedAttachment: note.image,
                              totalAttachments: 1,
                            }}
                            onClickAttachment={() => onImageClicked(x, note)}
                            fromMessages
                            fileName={note.image.split('.com/')[1]}
                          />
                        </div>
                      ) : (
                        <div
                          className="note-card-attachment"
                          style={{ cursor: 'pointer' }}
                          onClick={() => onImageClicked(x, note)}
                        >
                          {note.image.split('.com/')[1]}
                        </div>
                      )
                    ) : null}
                  </div>
                  {canShowActions && (
                    <div className="note-card-actions">
                      {enableEdit && canEditNote(note) && (
                        <Button
                          style={{ marginRight: '0.25rem' }}
                          color="semantic"
                          onClick={() => onEdit(note)}
                          label={<FontAwesomeIcon glyph="pencil" weight="light" />}
                        />
                      )}
                      {!hideDelete && canDeleteNote(note) && (
                        <Button
                          color="semantic"
                          onClick={() => setNoteToDelete(note)}
                          label={<FontAwesomeIcon glyph="trash" weight="light" />}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <GenericDialogue
        isOpen={!!expandedNote}
        onClose={() => setExpandedNote(null)}
        title={`${expandedNote?.author?.firstName ?? ''} ${expandedNote?.author?.lastName ?? ''}`.trim()}
        description={moment(expandedNote?.createdAt).format('MM/DD/YYYY hh:mm A')}
        maxWidth="80rem"
        avatar={<Avatar size="badge" />}
      >
        <div className="note-expanded-body">
          {expandedNote?.title && (
            <p className="note-expanded-title">{expandedNote.title}</p>
          )}
          {expandedNote?.image && expandedNote.image.length > 1 && (
            <div onClick={() => window.open(expandedNote.image, '_blank')} style={{ cursor: 'pointer' }}>
              {isImage(expandedNote.image) ? (
                <Attachments
                  attachments={{
                    hasAttachment: true,
                    firstCompressedAttachment: expandedNote.image,
                    totalAttachments: 1,
                  }}
                  onClickAttachment={() => window.open(expandedNote.image, '_blank')}
                  fromMessages
                  fileName={expandedNote.image.split('.com/')[1]}
                />
              ) : (
                <Attachments
                  attachments={{ hasAttachment: true, totalAttachments: 1 }}
                  onClickAttachment={() => window.open(expandedNote.image, '_blank')}
                  fromMessages
                  isPdf={expandedNote.image.toLowerCase().includes('.pdf')}
                  fileName={expandedNote.image.split('.com/')[1]}
                />
              )}
            </div>
          )}
          <TextEditor
            editorState={expandedNote?.body ?? ''}
            readOnly={true}
            onEditorStateChange={null}
          />
        </div>
      </GenericDialogue>
      {noteToDelete && (
        <ConfirmationModal
          title="Delete Note"
          message="Are you sure you want to delete this note?"
          warningMessage="This action cannot be undone and the note will be permanently removed."
          confirmLabel="Delete Note"
          cancelLabel="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          variant="danger"
          requireTypeToConfirm={true}
          confirmationWord="DELETE"
        />
      )}
      {noteToPin && (
        <ConfirmationModal
          title="Pin Note"
          message={pinConfirmMessage}
          warningMessage="You can pin one note at a time — this replaces any note currently pinned. Your pin is only visible to you."
          confirmLabel="Pin Note"
          cancelLabel="Cancel"
          onConfirm={handleConfirmPin}
          onCancel={handleCancelPin}
          isSubmitting={isPinning}
          variant="warning"
        />
      )}
    </>
  );
};

export default NotesRepeater;
